import { useContext, useEffect, useState } from 'react';
import { TFolder, TItem, TPath, TSelectItemDialogState, SelectItemDialog, TItemType } from '@modules/items';
import { FULL_PATH_SETUP_ITEM } from '@modules/items';
import { SignInContext } from '@modules/users';
import { useCancelInlineBlockDetection, useCreateInlineBlockDetection } from '../../api/inlineBlocks.api';
import LockedUsersDialog, { TLockedUsersDialogState } from '../shared/dialogs/LockedUsersDialog';
import { useSearchParams } from '@shared/hooks';
import { EDetectionViewMode } from '../../types/shared.types';
import { FULL_PATH_INLINE_BLOCK_DETECTION_ITEM } from '../../constants';
import InlineBlockDetectionPreview from './InlineBlockDetectionPreview';
import { openDialog, resetDialog } from '../../helpers/dialogs.helper';
import DetectionScaffold from '../shared/layout/DetectionScaffold';
import StartDetectionButton from '../shared/layout/StartDetectionButton';
import { TInlineBlockDetectionSelectableItemType } from '../../types/inlineBlocks.types';
import { SubsetOf } from '../../../../shared/types';

type Props = { folder: TFolder };

const InlineBlockDetectionContent = ({ folder }: Props) => {
  const { searchParams, change } = useSearchParams();
  const { organization } = useContext(SignInContext);

  // dynamic constants
  const rootItemId = folder._id;
  const isQm = organization.folders.qm === rootItemId;
  const libraryId = isQm ? organization.folders.qm : folder.blocksFolder?._id;
  const [runId, setRunId] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!runId) change('wide', undefined); // clean up
  }, [runId]);

  // nav + breadcrumbs path
  const fullPath: TPath = [
    { _id: rootItemId, name: folder.name, accessRight: folder.accessRight },
    FULL_PATH_SETUP_ITEM,
    FULL_PATH_INLINE_BLOCK_DETECTION_ITEM, // Inline-block detection
  ];

  // store the detection data in local state. ignore the "PENDING" polling status data
  const createMutation = useCreateInlineBlockDetection();
  const cancelMutation = useCancelInlineBlockDetection();

  // selections
  const [selectedInlineBlocks, setSelectedInlineBlocks] = useState<TItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<TItem[]>([]);

  const [lockedUsersDialogState, setLockedUsersDialogState] = useState<TLockedUsersDialogState>({
    isOpen: false,
    userIds: [],
  });

  const handleSelectInlineBlocks = async (localSelectedInlineBlocks: TItem[]) => {
    // use try-catch and `mutateAsync` to handle errors
    try {
      if (runId) await cancelMutation.mutateAsync({ runId });

      setRunId(undefined); // reset detection data to start fresh
      setSelectedInlineBlocks(localSelectedInlineBlocks);
      setItemsDialogState((prev) => ({
        ...prev,
        isOpen: true,
        onSelect: (localSelectedItems) => handleSelectedItems(localSelectedItems, localSelectedInlineBlocks),
      }));
    } catch (error) {
      console.error('Error during inline-block detection selection:', error); // dev only
    }
  };

  const handleSelectedItems = async (localSelectedItems: TItem[], localSelectedInlineBlocks: TItem[]) => {
    // use try-catch and `mutateAsync` to handle errors

    // --- MOCKING: remove when API is ready
    /*
    setViewMode(DetectionViewMode.PREVIEW);
    setRunId(MOCK_INLINE_BLOCK_RUN_ANALYZED.run._id);
    setDetectionData(MOCK_INLINE_BLOCK_RUN_ANALYZED);
    setItemsDialogState((prev) => prev && { ...prev, isOpen: false });
    setInlineBlocksDialogState((prev) => prev && { ...prev, isOpen: false });
    */
    try {
      await createMutation.mutateAsync(
        {
          rootItemId,
          selectedInlineBlocks: localSelectedInlineBlocks.map((item) => ({
            itemId: item._id,
            itemType: item.type as SubsetOf<TItemType, 'INLINE_BLOCK' | 'FOLDER'>,
          })),
          selectedItems: localSelectedItems.map((item) => ({
            itemId: item._id,
            itemType: item.type as TInlineBlockDetectionSelectableItemType,
          })),
        },
        {
          onSuccess: (data) => {
            if (data?.lockedUserIds) {
              setLockedUsersDialogState((prev) => ({ ...prev, isOpen: true, userIds: data.lockedUserIds }));
            } else {
              setItemsDialogState((prev) => prev && { ...prev, isOpen: false });
              setInlineBlocksDialogState((prev) => prev && { ...prev, isOpen: false });
              setRunId(data.runId);
            }
          },
        }
      );

      setSelectedItems(localSelectedItems);
    } catch (error) {
      console.error('Error during inline-block detection selection:', error); // dev only
    }
  };

  // dialogs
  const [inlineBlocksDialogState, setInlineBlocksDialogState] = useState<TSelectItemDialogState | undefined>({
    isOpen: false,
    onSelect: handleSelectInlineBlocks,
  });
  const [itemsDialogState, setItemsDialogState] = useState<TSelectItemDialogState | undefined>({
    isOpen: false,
    onSelect: (_) => Promise.resolve(), // dummy, real handler is set in `handleSelectInlineBlocks`
  });

  // Preselect inline block & auto-open dialog from URL params
  useEffect(() => {
    const inlineBlockId = searchParams.get('preselectedInlineBlock') ?? undefined;

    if (inlineBlockId) {
      const stub = { _id: inlineBlockId, type: 'INLINE_BLOCK' } as unknown as TItem;
      handleSelectInlineBlocks([stub]); // skip first dialog, go straight to items dialog
      change('preselectedInlineBlock', undefined); // clean up
    }
  }, []); // run once

  const handleStart = () => {
    openDialog(setInlineBlocksDialogState, handleSelectInlineBlocks); // open first step
    resetDialog(setItemsDialogState); // reset second step
  };

  return (
    <DetectionScaffold folder={folder} fullPath={fullPath} selectedValue="library-detection">
      {/* dialogs */}
      <SelectItemDialog
        initialFolderId={libraryId}
        initialSelectedItems={selectedInlineBlocks}
        state={inlineBlocksDialogState}
        setState={setInlineBlocksDialogState}
        multiSelect
        title="Select library items for detection"
        actionText="Continue"
        navigatableFolderTypes={['BLOCK']} // only blocks
        selectableTypes={['FOLDER', 'INLINE_BLOCK']} // user can select individual inline-blocks as well an entire folder
      />
      <SelectItemDialog
        initialFolderId={rootItemId}
        initialSelectedItems={selectedItems}
        state={itemsDialogState}
        setState={setItemsDialogState}
        multiSelect
        title="Select items for detection"
        actionText="Submit"
        navigatableFolderTypes={['NORMAL', 'COUNTRY']} // TODO: blocks might be added in the future
        selectableTypes={['FOLDER', 'DOCUMENT']}
      />
      <LockedUsersDialog state={lockedUsersDialogState} setState={setLockedUsersDialogState} />

      {/* main content area */}
      {!runId ? (
        <StartDetectionButton onStart={handleStart} />
      ) : (
        <InlineBlockDetectionPreview
          runId={runId}
          setRunId={setRunId}
          handleStart={handleStart}
          setSelectedItems={setSelectedItems}
        />
      )}
    </DetectionScaffold>
  );
};

export default InlineBlockDetectionContent;
